import { FC, useCallback } from "react";
import { useSelector } from "react-redux";
import { EmptyMessage } from "src/components";
import { PdTableCellText } from "src/components/layout/table";
import { useFocusableQuery } from "src/hooks/api";
import { tColumn } from "src/hooks/table";
import api from "src/util/apiNew";
import { formatMoney } from "src/util/gFormatting";
import { tRetailerReport } from "tmt_api/src/types/dtos";

import { PdSpinner, ReportTable } from "./components";
import { formatDate, formatDateFromMillis, getParams } from "./utils";

const getRetailersReport = (start: Date, end: Date) =>
  api.get<tRetailerReport[]>("reports/retailers", getParams(start, end)).then((res) => res.data);

const formatAvailableCredit = (row: tRetailerReport) => {
  if (!row.isSetupForTerms) return "N/A";
  return row.availableTermsCreditAmount ? formatMoney(row.availableTermsCreditAmount) : "-";
};

const formatTermsRepaymentStatus = (row: tRetailerReport) => {
  if (!row.termsRepaymentStatus) return "N/A";
  return row.termsRepaymentStatus === "goodStanding" ? "Good Standing" : "Past Due";
};

const columns: tColumn<tRetailerReport>[] = [
  {
    id: "acceptedDateInSeconds",
    header: "Activated Date",
    accessor: (originalRow) =>
      originalRow.acceptedDateInSeconds ? formatDate(originalRow.acceptedDateInSeconds) : "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 140 },
  },
  {
    id: "accountManagerName",
    header: "Account Manager",
    accessor: (originalRow) => originalRow.accountManagerName || "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "status",
    header: "Status",
    accessor: (originalRow) => (originalRow.isDeactivated ? "Inactive" : "Active"),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 140 },
  },
  {
    id: "name",
    header: "Business Name",
    accessor: (originalRow) => originalRow.name,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 240 },
  },
  {
    id: "contact.name",
    header: "Contact Name",
    accessor: (originalRow) => originalRow.contact?.name || "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 240 },
  },
  {
    id: "contact.email",
    header: "Contact Email",
    accessor: (originalRow) => originalRow.contact?.email || "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 300 },
  },
  {
    id: "phoneNumber",
    header: "Phone Number",
    accessor: (originalRow) => originalRow.phoneNumber || "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "website",
    header: "Website",
    accessor: (originalRow) => originalRow.website,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 300 },
  },
  {
    id: "instagram",
    header: "Instagram",
    accessor: (originalRow) => originalRow.instagram || "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "facebook",
    header: "Facebook",
    accessor: (originalRow) => originalRow.facebook || "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "availableTermsCredit",
    header: "Available NET Credit",
    accessor: (originalRow) => formatAvailableCredit(originalRow),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "isSetupForRewards",
    header: "Balance Vendor Connected",
    accessor: (originalRow) => (originalRow.isSetupForRewards ? "Yes" : "No"),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "totalBrandEarnedCreditAmount",
    header: "Brand Referral Credit Earned",
    accessor: (originalRow) =>
      originalRow.totalBrandEarnedCreditAmount ? formatMoney(originalRow.totalBrandEarnedCreditAmount) : "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "totalOtherEarnedCreditAmount",
    header: "Other Referral Earned",
    accessor: (originalRow) =>
      originalRow.totalOtherEarnedCreditAmount ? formatMoney(originalRow.totalOtherEarnedCreditAmount) : "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "availableEarnedCreditAmount",
    header: "Rewards Available",
    accessor: (originalRow) =>
      originalRow.availableEarnedCreditAmount ? formatMoney(originalRow.availableEarnedCreditAmount) : "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "creditOnHoldAmount",
    header: "Rewards on Hold",
    accessor: (originalRow) => (originalRow.creditOnHoldAmount ? formatMoney(originalRow.creditOnHoldAmount) : "-"),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "termsRepaymentStatus",
    header: "Balance Payment Status",
    accessor: (originalRow) => formatTermsRepaymentStatus(originalRow),
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "hearAboutUs",
    header: "How did they find us?",
    accessor: (originalRow) => originalRow.hearAboutUs || "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "lastOrderedDateInMillis",
    header: "Last Ordered",
    accessor: (originalRow) =>
      originalRow.lastOrderedDateInMillis ? formatDateFromMillis(originalRow.lastOrderedDateInMillis) : "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "internalRank",
    header: "Internal Rank",
    accessor: (originalRow) => originalRow.internalRank || "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "internalPrimaryCategory",
    header: "Primary Category",
    accessor: (originalRow) => originalRow.internalPrimaryCategory || "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "internalSecondaryCategory",
    header: "Secondary Category",
    accessor: (originalRow) => originalRow.internalSecondaryCategory || "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "internalTertiaryCategory",
    header: "Tertiary Category",
    accessor: (originalRow) => originalRow.internalTertiaryCategory || "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "address1",
    header: "Address Line 1",
    accessor: (originalRow) => originalRow.primaryAddressLine1,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "address2",
    header: "Address Line 2",
    accessor: (originalRow) => originalRow.primaryAddressLine2 || "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "city",
    header: "City",
    accessor: (originalRow) => originalRow.primaryAddressCity,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "state",
    header: "State",
    accessor: (originalRow) => originalRow.primaryAddressState,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "postalCode",
    header: "Zip Code",
    accessor: (originalRow) => originalRow.primaryAddressZipCode,
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 200 },
  },
  {
    id: "pricePoints",
    header: "Average Retail Price Point of your Brands",
    accessor: (originalRow) => originalRow.pricePoints || "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 400 },
  },
  {
    id: "locationsQuantity",
    header: "How many retail locations do you have?",
    accessor: (originalRow) => originalRow.locationsQuantity || "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 400 },
  },
  {
    id: "internalNotes",
    header: "Internal Notes",
    accessor: (originalRow) => originalRow.internalNotes || "-",
    Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
    style: { width: 400 },
  },
];

const RetailersTable: FC<{ period: Date[] }> = ({ period }) => {
  const config = useSelector((state) => state.TableConfigReducer.retailersExcelFormat);
  const { isLoading, data } = useFocusableQuery<tRetailerReport[]>(
    useCallback(() => getRetailersReport(period[0], period[1]), [period]),
    { enabled: !!(period[0] && period[1]) },
  );

  if (isLoading) {
    return <PdSpinner />;
  }

  if (!data || data?.length === 0) {
    return <EmptyMessage message="There is no data to display" />;
  }

  return (
    <ReportTable
      data={data}
      columns={columns}
      keyExtractor={(item) => item.id}
      sortedBy="acceptedDateInSeconds"
      columnsConfig={config}
      tableConfigReduxKey="retailersExcelFormat"
    />
  );
};

export default RetailersTable;
